import { useState, useCallback, useEffect } from 'react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Modal, Button, Select, Input } from '@/ui'
import styles from './EditModelModal.module.css'

interface SourceModel {
  id: string
  name: string
  provider: string
  providerType: string
  providerModel: string
  modelType: 'image' | 'text'
  costPerImage?: number
  facePreservation?: string
  speed?: string
  supportsReferenceImage?: boolean
  aspectRatio?: string | null
  imageSize?: string | null
  description?: string
  costPer1kInputTokens?: number
  costPer1kOutputTokens?: number
  maxContextTokens?: number
}

interface CreateVariantModalProps {
  open: boolean
  sourceModel: SourceModel | null
  onClose: () => void
  onCreated: () => void
}

const FACE_OPTIONS = [
  { value: 'excellent', label: 'excellent' },
  { value: 'good', label: 'good' },
  { value: 'fair', label: 'fair' },
  { value: 'poor', label: 'poor' },
]

const SPEED_OPTIONS = [
  { value: 'fast', label: 'fast' },
  { value: 'medium', label: 'medium' },
  { value: 'slow', label: 'slow' },
]

const ASPECT_RATIO_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
]

const IMAGE_SIZE_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '512', label: '512' },
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
]

interface ImageForm {
  modelId: string
  name: string
  costPerImage: string
  description: string
  facePreservation: string
  speed: string
  supportsReferenceImage: boolean
  aspectRatio: string
  imageSize: string
}

interface TextForm {
  modelId: string
  name: string
  costPer1kInputTokens: string
  costPer1kOutputTokens: string
  maxContextTokens: string
  description: string
}

export function CreateVariantModal({ open, sourceModel, onClose, onCreated }: CreateVariantModalProps) {
  const { addToast } = useToast()
  const [imageForm, setImageForm] = useState<ImageForm | null>(null)
  const [textForm, setTextForm] = useState<TextForm | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!sourceModel) {
      setImageForm(null)
      setTextForm(null)
      return
    }
    const variantId = `${sourceModel.id}-v2`
    const variantName = `${sourceModel.name} (변형)`
    if (sourceModel.modelType === 'image') {
      setImageForm({
        modelId: variantId,
        name: variantName,
        costPerImage: String(sourceModel.costPerImage ?? 0),
        description: sourceModel.description ?? '',
        facePreservation: sourceModel.facePreservation ?? 'good',
        speed: sourceModel.speed ?? 'medium',
        supportsReferenceImage: sourceModel.supportsReferenceImage ?? true,
        aspectRatio: sourceModel.aspectRatio ?? '',
        imageSize: sourceModel.imageSize ?? '',
      })
      setTextForm(null)
    } else {
      setTextForm({
        modelId: variantId,
        name: variantName,
        costPer1kInputTokens: String(sourceModel.costPer1kInputTokens ?? 0),
        costPer1kOutputTokens: String(sourceModel.costPer1kOutputTokens ?? 0),
        maxContextTokens: String(sourceModel.maxContextTokens ?? 0),
        description: sourceModel.description ?? '',
      })
      setImageForm(null)
    }
  }, [sourceModel])

  const handleCreate = useCallback(async () => {
    if (!sourceModel) return
    setIsSaving(true)
    try {
      const base = {
        providerType: sourceModel.providerType,
        providerModel: sourceModel.providerModel,
        modelType: sourceModel.modelType,
      }
      const body =
        sourceModel.modelType === 'image' && imageForm
          ? {
              ...base,
              modelId: imageForm.modelId,
              name: imageForm.name,
              costPerImage: parseFloat(imageForm.costPerImage),
              description: imageForm.description,
              facePreservation: imageForm.facePreservation,
              speed: imageForm.speed,
              supportsReferenceImage: imageForm.supportsReferenceImage,
              aspectRatio: imageForm.aspectRatio || null,
              imageSize: imageForm.imageSize || null,
            }
          : textForm
            ? {
                ...base,
                modelId: textForm.modelId,
                name: textForm.name,
                costPer1kInputTokens: parseFloat(textForm.costPer1kInputTokens),
                costPer1kOutputTokens: parseFloat(textForm.costPer1kOutputTokens),
                maxContextTokens: parseInt(textForm.maxContextTokens, 10),
                description: textForm.description,
              }
            : null

      if (!body) return

      await apiFetch('/api/admin/ai-models/register', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      addToast({ type: 'success', message: `${body.name} 변형이 등록되었습니다.` })
      onCreated()
    } catch (e: any) {
      addToast({ type: 'error', message: e.message })
    } finally {
      setIsSaving(false)
    }
  }, [sourceModel, imageForm, textForm, addToast, onCreated])

  if (!sourceModel) return null

  return (
    <Modal open={open} onClose={onClose} title="변형 추가" size="md">
      <div className={styles.root}>
        {sourceModel.modelType === 'image' && imageForm && (
          <>
            <div className={styles.formGrid}>
              <Input
                size="sm"
                label="Model ID"
                value={imageForm.modelId}
                onChange={(e) => setImageForm((f) => f && { ...f, modelId: e.target.value })}
              />
              <Input
                size="sm"
                label="이름"
                value={imageForm.name}
                onChange={(e) => setImageForm((f) => f && { ...f, name: e.target.value })}
              />
              <Input
                size="sm"
                label="비용/이미지 ($)"
                type="number"
                step="0.01"
                value={imageForm.costPerImage}
                onChange={(e) => setImageForm((f) => f && { ...f, costPerImage: e.target.value })}
              />
              <Select
                size="sm"
                label="얼굴 보존"
                value={imageForm.facePreservation}
                onChange={(e) =>
                  setImageForm((f) => f && { ...f, facePreservation: e.target.value })
                }
                options={FACE_OPTIONS}
              />
              <Select
                size="sm"
                label="속도"
                value={imageForm.speed}
                onChange={(e) => setImageForm((f) => f && { ...f, speed: e.target.value })}
                options={SPEED_OPTIONS}
              />
              <Select
                size="sm"
                label="비율 (aspectRatio)"
                value={imageForm.aspectRatio}
                onChange={(e) => setImageForm((f) => f && { ...f, aspectRatio: e.target.value })}
                options={ASPECT_RATIO_OPTIONS}
              />
              <Select
                size="sm"
                label="해상도 (imageSize)"
                value={imageForm.imageSize}
                onChange={(e) => setImageForm((f) => f && { ...f, imageSize: e.target.value })}
                options={IMAGE_SIZE_OPTIONS}
              />
            </div>
            <Input
              size="sm"
              label="설명"
              value={imageForm.description}
              onChange={(e) => setImageForm((f) => f && { ...f, description: e.target.value })}
            />
            <div className={styles.checkboxField}>
              <label>
                <input
                  type="checkbox"
                  checked={imageForm.supportsReferenceImage}
                  onChange={(e) =>
                    setImageForm((f) => f && { ...f, supportsReferenceImage: e.target.checked })
                  }
                />
                참조 이미지 지원
              </label>
            </div>
          </>
        )}

        {sourceModel.modelType === 'text' && textForm && (
          <>
            <div className={styles.formGrid}>
              <Input
                size="sm"
                label="Model ID"
                value={textForm.modelId}
                onChange={(e) => setTextForm((f) => f && { ...f, modelId: e.target.value })}
              />
              <Input
                size="sm"
                label="이름"
                value={textForm.name}
                onChange={(e) => setTextForm((f) => f && { ...f, name: e.target.value })}
              />
              <Input
                size="sm"
                label="Max Context Tokens"
                type="number"
                value={textForm.maxContextTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, maxContextTokens: e.target.value })
                }
              />
              <Input
                size="sm"
                label="Input 비용 ($/1k tokens)"
                type="number"
                step="0.0001"
                value={textForm.costPer1kInputTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, costPer1kInputTokens: e.target.value })
                }
              />
              <Input
                size="sm"
                label="Output 비용 ($/1k tokens)"
                type="number"
                step="0.0001"
                value={textForm.costPer1kOutputTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, costPer1kOutputTokens: e.target.value })
                }
              />
            </div>
            <Input
              size="sm"
              label="설명"
              value={textForm.description}
              onChange={(e) => setTextForm((f) => f && { ...f, description: e.target.value })}
            />
          </>
        )}

        <div className={styles.actions}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" variant="primary" onClick={handleCreate} loading={isSaving}>
            등록
          </Button>
        </div>
      </div>
    </Modal>
  )
}
